import {
  Box, Button, Collapse, Divider, Popover, Stack, Typography, type PopoverActions,
} from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { DriverEntity } from 'src/entities/Driver.entity';
import { ShiftPatternEntity } from 'src/entities/ShiftPattern.entity';
import {
  AddRequestBody, buildAddRequest, buildEditRequest, cellEditorSchema, CellEditorFormValues,
  driverChanged, EditRequestBody, editFieldsChanged, isShiftEditable,
  resolveDriver, summarizeClearCell, toEditorValues,
} from 'src/pages/TruckShifts/cellEditor';
import { CellAddPicker } from 'src/pages/TruckShifts/presenters/CellAddPicker.presenter';
import { CellShiftCard } from 'src/pages/TruckShifts/presenters/CellShiftCard.presenter';
import { isTruncatableAtDate, ShiftCell } from 'src/pages/TruckShifts/truckShiftGrid';

type Props = {
  open: boolean;
  anchorEl: HTMLElement | null;
  truckId: number;
  date: string;
  truckName: string;
  cells: ShiftCell[];
  patternOptions: ShiftPatternEntity[];
  allPatterns: ShiftPatternEntity[];
  driverOptions: DriverEntity[];
  onClose: () => void;
  editShift: (id: number, body: EditRequestBody) => Promise<void>;
  assignDriver?: (id: number, driverId: number | null) => Promise<void>;
  createDriver?: (name: string) => Promise<{ id: number; name: string }>;
  addShift?: (body: AddRequestBody) => Promise<void>;
  deleteShift: (id: number) => Promise<void>;
  truncateShift?: (id: number, on: string) => Promise<void>;
  onNotify: (message: string, variant: 'success' | 'error') => void;
  extractError: (rejection: PromiseRejectedResult) => string;
  /** false で「別の予定を追加」と空セルの追加ピッカーを出さない。default true */
  allowAddShift?: boolean;
  /** false でドライバーの変更欄を出さない。default true */
  allowDriverEdit?: boolean;
  /** false で「打ち切り」を出さない。default true */
  allowTruncate?: boolean;
};

const toRejection = (reason: unknown): PromiseRejectedResult => ({ status: 'rejected', reason });

const UNASSIGN_CONFIRM = '既に配車した荷物は、未割り当てに戻りますがよろしいですか？';

// 制限モード時は対応する操作 UI 自体を描かないため、これらの既定値には到達しない。
// createDriver は戻り値の driver id が必要で no-op を置けないため、
// 万一到達したら null 割当で握り潰さず失敗させる（誤ってドライバーを外さない）。
const noopAsync = async () => {};
const unavailableCreateDriver = (): Promise<{ id: number; name: string }> => {
  throw new Error('ドライバーの新規登録はこの画面では行えません');
};

const initDrafts = (
  cells: ShiftCell[],
  patternOptions: ShiftPatternEntity[],
): Record<number, CellEditorFormValues> =>
  cells.reduce<Record<number, CellEditorFormValues>>((acc, { shift }) => {
    acc[shift.id] = toEditorValues(shift, patternOptions).values;
    return acc;
  }, {});

export const CellEditorPopover: FC<Props> = ({
  open, anchorEl, truckId, date, truckName, cells, patternOptions, allPatterns, driverOptions, onClose,
  editShift, deleteShift, onNotify, extractError,
  assignDriver = noopAsync,
  createDriver = unavailableCreateDriver,
  addShift = noopAsync,
  truncateShift = noopAsync,
  allowAddShift = true,
  allowDriverEdit = true,
  allowTruncate = true,
}) => {
  const [drafts, setDrafts] = useState<Record<number, CellEditorFormValues>>(() =>
    initDrafts(cells, patternOptions),
  );
  const initialDrafts = useRef<Record<number, CellEditorFormValues>>(initDrafts(cells, patternOptions));
  const [submitting, setSubmitting] = useState(false);
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  // Popover は開いた時のみビューポート補正が入るため、開いた後にコンテンツが伸びると下端がはみ出す。
  // action ref 経由で再補正（updatePosition）し、下端を画面内へ収める。
  const popoverActionRef = useRef<PopoverActions>(null);
  const repositionPopover = () => popoverActionRef.current?.updatePosition();

  // cells が変わったら drafts を再初期化（ライブ更新）
  // shiftPattern の id も含めることでパターン変更を検出する
  // patternOptions は cellsKey に含まないが、patternOptions の変化は
  // セルクリックによる openCell の再セットで cellsKey も変わるため実害なし。
  const cellsKey = cells.map((c) => `${c.shift.id}:${c.shift.shiftPattern?.id ?? 'null'}`).join(',');
  useEffect(() => {
    const next = initDrafts(cells, patternOptions);
    setDrafts(next);
    initialDrafts.current = next;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellsKey]);

  // Popover は閉じても unmount されず state が残るため、閉じたら「別の予定を追加」の開閉状態を
  // リセットする（キャンセル後に再度開いたときは既定の閉じた状態で表示する）。
  useEffect(() => {
    if (!open) setAddPickerOpen(false);
  }, [open]);

  const canSave = !submitting && cells.every(({ shift }) => {
    const draft = drafts[shift.id];
    if (!draft) return false;
    return cellEditorSchema.isValidSync(draft);
  });

  const isDirty = cells.some(({ shift }) => {
    const initial = initialDrafts.current[shift.id];
    const current = drafts[shift.id];
    if (!initial || !current) return false;
    return editFieldsChanged(initial, current) || driverChanged(initial, current);
  });

  const handlePickPattern = async (patternId: number) => {
    const values: CellEditorFormValues = {
      mode: 'pattern', patternId, startTime: '', endTime: '', startDate: date, endDate: date, workingHours: '', driverId: null, driverName: '',
    };
    onClose(); // 便を選択したら即閉じ（楽観クローズ・結果はトースト通知）
    try {
      await addShift(buildAddRequest(truckId, date, values));
      onNotify('追加しました', 'success');
    } catch (e) {
      onNotify(extractError(toRejection(e)), 'error');
    }
  };

  const handleAddCustom = async (startTime: string, endTime: string) => {
    const values: CellEditorFormValues = {
      mode: 'custom', patternId: null, startTime, endTime, startDate: date, endDate: date, workingHours: '', driverId: null, driverName: '',
    };
    onClose(); // 選択したら即閉じ（楽観クローズ・結果はトースト通知）
    try {
      await addShift(buildAddRequest(truckId, date, values));
      onNotify('追加しました', 'success');
    } catch (e) {
      onNotify(extractError(toRejection(e)), 'error');
    }
  };

  const handleTruncate = async (shiftId: number) => {
    onClose(); // 楽観クローズ
    try {
      await truncateShift(shiftId, date);
      onNotify('打ち切りました', 'success');
    } catch (e) {
      onNotify(extractError(toRejection(e)), 'error');
    }
  };

  const handleDeleteShift = async (id: number) => {
    const dispatched = cells.find((c) => c.shift.id === id)?.shift.dispatched ?? false;
    if (!window.confirm(dispatched ? UNASSIGN_CONFIRM : 'この勤務を削除しますか？')) return;
    onClose(); // 削除完了を待たず即閉じ（空セルのピッカー表示に遷移させない）
    try {
      await deleteShift(id);
      onNotify('削除しました', 'success');
    } catch (e) {
      onNotify(extractError(toRejection(e)), 'error');
    }
  };

  const handleClearCell = async () => {
    const anyDispatched = cells.some((c) => c.shift.dispatched);
    if (!window.confirm(anyDispatched ? UNASSIGN_CONFIRM : 'このセルの勤務を全て削除しますか？')) return;
    onClose(); // クリア完了を待たず即閉じ（空セルのピッカー表示に遷移させない）
    const results = await Promise.allSettled(cells.map(({ shift }) => deleteShift(shift.id)));
    const summary = summarizeClearCell(results, extractError);
    onNotify(summary.message, summary.failed === 0 ? 'success' : 'error');
  };

  // キャンセルボタン: cells.length>0 の表示時のみ表示されるため isDirty だけでガード
  const handleCancel = () => {
    if (isDirty && !window.confirm('変更を破棄してもよろしいですか？')) return;
    onClose();
  };

  const handleSave = async () => {
    const dirtyShifts = cells.filter(({ shift }) => {
      const initial = initialDrafts.current[shift.id];
      const current = drafts[shift.id];
      if (!initial || !current) return false;
      return editFieldsChanged(initial, current) || driverChanged(initial, current);
    });

    // 新規ドライバー作成の事前確認（editShift より前）
    for (const { shift } of dirtyShifts) {
      const values = drafts[shift.id];
      const initial = initialDrafts.current[shift.id];
      if (initial && driverChanged(values, initial)) {
        const resolution = resolveDriver(values, driverOptions);
        if (resolution.kind === 'create') {
          if (!window.confirm(`「${resolution.name}」を新規登録しますか？`)) return;
        }
      }
    }

    setSubmitting(true);
    const errors: string[] = [];

    for (const { shift } of dirtyShifts) {
      const values = drafts[shift.id];
      const initial = initialDrafts.current[shift.id];
      // dirtyShifts フィルタで !initial || !current をガード済みだが
      // TS の narrowing がフィルタを跨がないため明示的に skip する
      if (!values || !initial) continue;
      const editable = isShiftEditable(shift);

      if (editable && editFieldsChanged(values, initial)) {
        try {
          await editShift(shift.id, buildEditRequest(values));
        } catch (e) {
          errors.push(extractError(toRejection(e)));
          continue;
        }
      }

      if (driverChanged(values, initial)) {
        try {
          const resolution = resolveDriver(values, driverOptions);
          let driverId: number | null;
          if (resolution.kind === 'create') driverId = (await createDriver(resolution.name)).id;
          else if (resolution.kind === 'existing') driverId = resolution.driverId;
          else driverId = null;
          await assignDriver(shift.id, driverId);
        } catch (e) {
          errors.push(extractError(toRejection(e)));
        }
      }
    }

    setSubmitting(false);

    if (errors.length > 0) {
      onNotify(errors.join(' / '), 'error');
    } else {
      onNotify('保存しました', 'success');
      onClose();
    }
  };

  // Popover の外側クリック/ESC: cells=0（ピッカー表示）は isDirty=false のため条件なしで閉じる
  const requestClose = () => {
    if (cells.length > 0 && isDirty) {
      if (!window.confirm('変更を破棄してもよろしいですか？')) return;
    }
    onClose();
  };

  return (
    <Popover
      action={popoverActionRef}
      open={open}
      anchorEl={anchorEl}
      onClose={requestClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      {cells.length === 0 ? (
        allowAddShift ? (
          <CellAddPicker
            patterns={allPatterns}
            onPickPattern={(id) => { void handlePickPattern(id); }}
            onAddCustom={(s, e) => { void handleAddCustom(s, e); }}
          />
        ) : (
          <Box sx={{ p: 2, width: 280 }}>
            <Typography variant="body2" color="text.secondary">
              この日の勤務はありません。勤務の追加は勤務計画画面で行ってください。
            </Typography>
          </Box>
        )
      ) : (
        <Stack sx={{ p: 1.5, width: 380, maxHeight: '80vh', overflowY: 'auto' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {`${date} ${truckName}（${cells.length}件）`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            編集後、下部の「保存」を押してください
          </Typography>

          <Stack spacing={1.5}>
            {cells.map(({ shift }) => (
              <CellShiftCard
                key={shift.id}
                value={drafts[shift.id] ?? toEditorValues(shift, patternOptions).values}
                onChange={(v) => setDrafts((prev) => ({ ...prev, [shift.id]: v }))}
                editFieldsLocked={!isShiftEditable(shift)}
                allPatterns={allPatterns}
                driverOptions={driverOptions}
                deletable={isShiftEditable(shift)}
                onDelete={() => { void handleDeleteShift(shift.id); }}
                cellDate={date}
                truncatable={allowTruncate && isTruncatableAtDate(shift, date, allPatterns)}
                onTruncate={() => { void handleTruncate(shift.id); }}
                allowDriverEdit={allowDriverEdit}
              />
            ))}
          </Stack>

          {/* 別の予定を追加（折りたたみ） */}
          {allowAddShift && (
            <Box sx={{ mt: 1.5 }}>
              <Button size="small" onClick={() => setAddPickerOpen((prev) => !prev)}>
                ＋ 別の予定を追加
              </Button>
              <Collapse in={addPickerOpen} unmountOnExit onEntered={repositionPopover} onExited={repositionPopover}>
                <CellAddPicker
                  patterns={allPatterns}
                  onPickPattern={(id) => { void handlePickPattern(id); }}
                  onAddCustom={(s, e) => { void handleAddCustom(s, e); }}
                />
              </Collapse>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          {/* 下部バー */}
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Button size="small" color="error" onClick={() => { void handleClearCell(); }}>
              🗑️ 全クリア
            </Button>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={handleCancel}>キャンセル</Button>
              <Button size="small" variant="contained" onClick={() => { void handleSave(); }} disabled={!canSave}>
                保存
              </Button>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Popover>
  );
};
